import type { Node as PMNode, Schema } from "@tiptap/pm/model";
import { type EditorState, TextSelection, type Transaction } from "@tiptap/pm/state";
import { equalWidths, isUniform } from "./table-math";

export type CellIndex = { row: number; col: number };

export function findCellIndex(state: EditorState, tablePos: number): CellIndex | null {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === "klTable" && $from.before(d) === tablePos) {
      if ($from.depth < d + 2) return null;
      return { row: $from.index(d), col: $from.index(d + 1) };
    }
  }
  return null;
}

const tableAt = (state: EditorState, pos: number): PMNode | null => {
  const n = state.doc.nodeAt(pos);
  return n && n.type.name === "klTable" ? n : null;
};
const children = (n: PMNode): PMNode[] => {
  const out: PMNode[] = [];
  n.forEach((c) => {
    out.push(c);
  });
  return out;
};
const columnsOf = (table: PMNode) => table.firstChild?.childCount ?? 1;
const widthsOf = (table: PMNode): number[] => {
  const w = table.attrs.widths as number[] | null;
  return w && w.length === columnsOf(table) ? [...w] : equalWidths(columnsOf(table));
};
const emptyCell = (schema: Schema): PMNode => {
  const cell = schema.nodes.klTableCell?.createAndFill();
  if (!cell) throw new Error("klTableCell cannot be created");
  return cell;
};

function replaceTable(
  state: EditorState,
  pos: number,
  table: PMNode,
  rows: PMNode[],
  attrs: PMNode["attrs"],
): Transaction {
  const next = table.type.createChecked(attrs, rows);
  const tr = state.tr.replaceWith(pos, pos + table.nodeSize, next);
  const from = Math.min(state.selection.from, tr.doc.content.size);
  return tr.setSelection(TextSelection.near(tr.doc.resolve(from)));
}

export function addRow(state: EditorState, pos: number, cell: CellIndex | null): Transaction | null {
  const table = tableAt(state, pos);
  const rowType = state.schema.nodes.klTableRow;
  if (!table || !rowType) return null;
  const rows = children(table);
  const row = rowType.createChecked(
    null,
    Array.from({ length: columnsOf(table) }, () => emptyCell(state.schema)),
  );
  rows.splice(cell ? cell.row + 1 : rows.length, 0, row);
  return replaceTable(state, pos, table, rows, table.attrs);
}

export function deleteRow(state: EditorState, pos: number, cell: CellIndex | null): Transaction | null {
  const table = tableAt(state, pos);
  if (!table || !cell || cell.row === 0) return null; // the header row stays (spec §4.1)
  const rows = children(table);
  if (rows.length <= 2) return null; // keep at least one body row
  rows.splice(cell.row, 1);
  return replaceTable(state, pos, table, rows, table.attrs);
}

export function addColumn(state: EditorState, pos: number, cell: CellIndex | null): Transaction | null {
  const table = tableAt(state, pos);
  if (!table) return null;
  const at = cell ? cell.col + 1 : columnsOf(table);
  const rows = children(table).map((row) => {
    const cells = children(row);
    cells.splice(at, 0, emptyCell(state.schema));
    return row.type.createChecked(row.attrs, cells);
  });
  const widths = widthsOf(table);
  const avg = widths.reduce((a, b) => a + b, 0) / widths.length;
  widths.splice(at, 0, Math.round(avg * 1000) / 1000);
  return replaceTable(state, pos, table, rows, { ...table.attrs, widths: isUniform(widths) ? null : widths });
}

export function deleteColumn(state: EditorState, pos: number, cell: CellIndex | null): Transaction | null {
  const table = tableAt(state, pos);
  if (!table || !cell || columnsOf(table) <= 1) return null;
  const rows = children(table).map((row) => {
    const cells = children(row);
    cells.splice(cell.col, 1);
    return row.type.createChecked(row.attrs, cells);
  });
  const widths = widthsOf(table);
  widths.splice(cell.col, 1);
  return replaceTable(state, pos, table, rows, { ...table.attrs, widths: isUniform(widths) ? null : widths });
}

export function toggleFirstColBold(state: EditorState, pos: number): Transaction | null {
  const table = tableAt(state, pos);
  if (!table) return null;
  return state.tr.setNodeMarkup(pos, undefined, { ...table.attrs, firstColBold: table.attrs.firstColBold === false });
}
